import { defineMessages } from '../../i18n/messages';
import { englishText } from '../../utils/messageFallbacks';

const FAQ_DEFINITIONS = {
  TITLE: {
    id: 'homepage.faq.title',
    text: 'Frequently Asked Questions',
    description: 'Section heading for the FAQ section on the homepage.',
  },
  SUBTITLE: {
    id: 'homepage.faq.subtitle',
    text: 'Everything you need to know about Localingos',
    description: 'Subtitle below the FAQ heading. "Localingos" is the product name, do not translate.',
  },
  Q_WHY_NOT_LLM: {
    id: 'homepage.faq.q_why_not_llm',
    text: 'Why not just use ChatGPT or Claude directly?',
    description: 'FAQ question handling the most common developer objection: that they could paste their strings into a chat assistant themselves. "ChatGPT" and "Claude" are product names, do not translate.',
  },
  A_WHY_NOT_LLM: {
    id: 'homepage.faq.a_why_not_llm',
    // eslint-disable-next-line no-template-curly-in-string
    text: 'You can, and the output will be comparable — the same class of model does the translating either way, so quality is not the differentiator. The pipeline around it is: sync re-translates only the strings that changed, every response is checked to confirm your placeholders (${count}, %s, {{name}}) survived and retried with a corrective prompt if they did not, output is forced to JSON at temperature 0, and each locale runs in isolation so one bad language cannot fail the batch. It also runs where you already work: CLI, GitHub Actions, REST API, MCP server, 56 locales, per-word usage accounting. A chat window gives you a translation; this gives you a repeatable pipeline.',
    // eslint-disable-next-line no-template-curly-in-string
    description: 'FAQ answer conceding that a chat assistant produces similar translations, then explaining the surrounding automation. "${count}", "%s" and "{{name}}" are code placeholder examples and must NOT be translated. "sync" is the name of a CLI command, keep it as-is. "JSON", "temperature 0", "CLI", "REST API", "MCP" and "GitHub Actions" are technical terms.',
  },
  Q1: {
    id: 'homepage.faq.q1',
    text: 'How do you protect my placeholders/variables?',
    // eslint-disable-next-line no-template-curly-in-string
    description: 'FAQ question about placeholder preservation. "Placeholders" and "variables" are code tokens like ${name} or {count} embedded in translatable strings.',
  },
  A1: {
    id: 'homepage.faq.a1',
    // eslint-disable-next-line no-template-curly-in-string
    text: 'We ensure placeholders like ${days}, %s, {count} remain intact during translation using advanced parsing that recognizes and preserves variable syntax. Our system identifies common placeholder patterns and treats them as protected elements that should not be translated.',
    // eslint-disable-next-line no-template-curly-in-string
    description: 'FAQ answer about placeholder preservation. "${days}", "%s", and "{count}" are code placeholder examples and should NOT be translated. Explains the technical approach to keeping variables safe.',
  },
  Q2: {
    id: 'homepage.faq.q2',
    text: 'How fast is it really?',
    description: 'FAQ question about translation speed. Casual, conversational tone.',
  },
  A2: {
    id: 'homepage.faq.a2',
    text: 'Most jobs complete in seconds; our system handles 1,000+ translations per minute with LLM-powered automation. Unlike human translators who may take days or weeks, our automated pipeline processes your content immediately while maintaining high quality.',
    description: 'FAQ answer about speed. "LLM" stands for Large Language Model. "1,000+" means more than one thousand. Contrasts automated speed with traditional human translation timelines.',
  },
  Q3: {
    id: 'homepage.faq.q3',
    text: 'How does pricing work?',
    description: 'FAQ question about the pricing model.',
  },
  A3: {
    id: 'homepage.faq.a3',
    text: 'We offer a free tier with 5,000 words included, no credit card required. After that, choose an annual plan: Starter ($9/mo, 50k words/year), Growth ($29/mo, 250k words/year), or Scale ($79/mo, 1M words/year). All plans are billed annually and include unlimited storage, 56 locales, and the free Localingos CLI.',
    description: 'FAQ answer about pricing. Lists all plan tiers with prices in US dollars. "$9/mo" means 9 dollars per month. "50k" means 50,000. "1M" means 1,000,000. "CLI" is Command Line Interface. Plan names (Starter, Growth, Scale) should not be translated.',
  },
  Q6: {
    id: 'homepage.faq.q6',
    text: 'Do you support glossaries/context?',
    description: 'FAQ question about glossary and context support. "Glossaries" are lists of preferred translations for specific terms. "Context" means additional information provided to improve translation quality.',
  },
  A6: {
    id: 'homepage.faq.a6',
    text: 'Absolutely. You can provide project context and custom glossaries to ensure consistent terminology across your translations. This helps maintain brand voice and technical accuracy in your localized content.',
    description: 'FAQ answer about glossary support. Explains that users can provide context and glossaries for better translation consistency.',
  },
  Q7: {
    id: 'homepage.faq.q7',
    text: 'What languages do you support?',
    description: 'FAQ question about supported languages.',
  },
  A7: {
    id: 'homepage.faq.a7',
    text: 'We support 56 locales including Spanish, French, German, Italian, Portuguese, Chinese, Japanese, Korean, Arabic, and many more. All language pairs maintain the same high quality and placeholder preservation standards.',
    description: 'FAQ answer about language support. Lists example languages. "56 locales" is the total number of supported language/region combinations. "Placeholder preservation" means code variables are kept intact.',
  },
  STILL_HAVE_QUESTIONS: {
    id: 'homepage.faq.still_have_questions',
    text: 'Still have questions?',
    description: 'Heading above the contact section at the bottom of the FAQ. Invites users who did not find their answer to reach out.',
  },
  STILL_HAVE_QUESTIONS_DESC: {
    id: 'homepage.faq.still_have_questions_desc',
    text: 'Our team is here to help. Reach out with any questions about implementation, pricing, or technical requirements.',
    description: 'Description below the "Still have questions?" heading. Encourages users to contact support.',
  },
  BTN_CONTACT_SUPPORT: {
    id: 'homepage.faq.btn_contact_support',
    text: 'Contact Support',
    description: 'Button label that opens a way to contact the support team.',
  },
  BTN_VIEW_DOCS: {
    id: 'homepage.faq.btn_view_docs',
    text: 'View Documentation',
    description: 'Button label that navigates to the product documentation.',
  },
};

export const FAQMessages = defineMessages(FAQ_DEFINITIONS);

/** English fallbacks, used until `npm run i18n:extract` adds these ids to en-US.json. */
export const FAQText = englishText(FAQ_DEFINITIONS);
